/**
 * Production-Ready Logger
 * 
 * Structured logging with correlation IDs and error tracking
 */

export interface LogContext {
  correlationId?: string;
  userId?: string;
  organizationId?: string;
  [key: string]: any;
}

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

export class Logger {
  private static instance: Logger;
  private level: LogLevel;
  private format: 'json' | 'text';

  constructor(level: string = 'info', format: 'json' | 'text' = 'json') {
    this.level = this.parseLevel(level);
    this.format = format;
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(
        process.env.LOG_LEVEL || 'info',
        (process.env.LOG_FORMAT || 'json') as any
      );
    }
    return Logger.instance;
  }

  private parseLevel(level: string): LogLevel {
    switch (level.toLowerCase()) {
      case 'error': return LogLevel.ERROR;
      case 'warn': return LogLevel.WARN;
      case 'info': return LogLevel.INFO;
      case 'debug': return LogLevel.DEBUG;
      default: return LogLevel.INFO;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.level;
  }

  private formatMessage(
    level: string,
    message: string,
    context?: LogContext,
    error?: Error
  ): string {
    const timestamp = new Date().toISOString();
    
    if (this.format === 'json') {
      const log: any = {
        timestamp,
        level,
        message,
        ...context
      };

      if (error) {
        log.error = {
          name: error.name,
          message: error.message,
          stack: error.stack
        };
      }

      return JSON.stringify(log);
    } else {
      let log = `[${timestamp}] [${level}] ${message}`;
      
      if (context?.correlationId) {
        log += ` [${context.correlationId}]`;
      }
      
      if (error) {
        log += `\n${error.stack}`;
      }
      
      return log;
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage('ERROR', message, context, error));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage('WARN', message, context));
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(this.formatMessage('INFO', message, context));
    }
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(this.formatMessage('DEBUG', message, context));
    }
  }

  /**
   * Create a child logger with fixed context
   */
  child(context: LogContext): Logger {
    const parent = this;
    return {
      error(message: string, error?: Error, additionalContext?: LogContext) {
        parent.error(message, error, { ...context, ...additionalContext });
      },
      warn(message: string, additionalContext?: LogContext) {
        parent.warn(message, { ...context, ...additionalContext });
      },
      info(message: string, additionalContext?: LogContext) {
        parent.info(message, { ...context, ...additionalContext });
      },
      debug(message: string, additionalContext?: LogContext) {
        parent.debug(message, { ...context, ...additionalContext });
      },
      child(additionalContext: LogContext) {
        return parent.child({ ...context, ...additionalContext });
      }
    } as Logger;
  }
}